export interface HistoryVersion {
  id?: string;
  history_token?: string;
  created_at: string;
  is_original?: boolean;
  is_current?: boolean;
  description?: string;
}

interface InvoiceHistoryCardProps {
  histories?: HistoryVersion[];
  selectedHistoryId: string | null;
  onSelectVersion: (historyToken: string | null) => void;
  onRestoreVersion?: (historyToken: string) => void;
  isRestoring?: boolean;
}

export default function InvoiceHistoryCard({
  histories = [],
  selectedHistoryId,
  onSelectVersion,
  onRestoreVersion,
  isRestoring = false,
}: InvoiceHistoryCardProps) {
  if (!histories || histories.length === 0) {
    return null;
  }

  // Format date helper
  const formatDate = (isoString: string) => {
    try {
      const d = new Date(isoString);
      return isNaN(d.getTime()) ? isoString : d.toLocaleString();
    } catch {
      return isoString;
    }
  };

  return (
    <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-card mb-6 overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-5 border-b border-brand-line dark:border-strokedark flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-2 h-2 rounded-full bg-brand-vib" />
          <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
            Revision History
          </h3>
          <span className="px-2.5 py-0.5 text-[10px] font-mono font-semibold bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 rounded-full">
            {histories.length} {histories.length === 1 ? 'VERSION' : 'VERSIONS'}
          </span>
        </div>

        {selectedHistoryId && (
          <button
            type="button"
            onClick={() => onSelectVersion(null)}
            className="text-xs font-mono font-semibold text-brand-vib hover:underline cursor-pointer"
          >
            Reset to Current
          </button>
        )}
      </div>

      {/* History Version List */}
      <div className="divide-y divide-brand-line dark:divide-strokedark p-3 sm:p-4 space-y-2">
        {histories.map((hist, idx) => {
          const histId = hist.id || hist.history_token || `version-${idx}`;
          const isCurrent = hist.is_current || (!hist.is_original && idx === 0 && !selectedHistoryId);
          const isOriginal = hist.is_original || idx === histories.length - 1;
          const isSelected =
            histId === selectedHistoryId ||
            (selectedHistoryId === null && isCurrent);

          return (
            <div
              key={histId}
              onClick={() => onSelectVersion(isCurrent ? null : histId)}
              className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl cursor-pointer transition-all border ${
                isSelected
                  ? 'bg-brand-blue-tint/50 border-brand-vib/30 dark:bg-brand-900/20 dark:border-brand-vib/30 shadow-xs'
                  : 'border-transparent hover:bg-brand-bg/60 dark:hover:bg-strokedark/40'
              }`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="invoice-version-selector"
                  checked={isSelected}
                  onChange={() => onSelectVersion(isCurrent ? null : histId)}
                  className="w-4 h-4 text-brand-vib accent-brand-vib focus:ring-brand-vib cursor-pointer"
                />
                <div>
                  <p className="font-mono text-xs font-bold text-brand-navy dark:text-white">
                    {formatDate(hist.created_at)}
                  </p>
                  {hist.description && (
                    <p className="text-xs text-brand-soft dark:text-bodydark2 mt-0.5">
                      {hist.description}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {isOriginal && (
                      <span className="text-[10px] uppercase font-mono px-2 py-0.5 bg-gray-100 text-gray-700 dark:bg-strokedark dark:text-bodydark rounded-md font-semibold">
                        Original Scan
                      </span>
                    )}
                    {isCurrent && (
                      <span className="text-[10px] uppercase font-mono px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 rounded-md font-semibold">
                        Current Version
                      </span>
                    )}
                    {isSelected && !isCurrent && (
                      <span className="text-[10px] uppercase font-mono px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 rounded-md font-semibold">
                        Previewing Past Version
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 self-end sm:self-center">
                {!isSelected ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectVersion(histId);
                    }}
                    className="text-xs font-semibold text-brand-vib hover:bg-brand-vib hover:text-white bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark px-3 py-1.5 rounded-lg shadow-xs transition-colors cursor-pointer"
                  >
                    Preview Version
                  </button>
                ) : !isCurrent ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-semibold text-amber-600 dark:text-amber-400">
                      Active Preview
                    </span>
                    {onRestoreVersion && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRestoreVersion(histId);
                        }}
                        disabled={isRestoring}
                        className="text-xs font-semibold text-white bg-brand-vib hover:bg-brand-vib-hover px-3 py-1.5 rounded-lg shadow-xs transition-colors cursor-pointer disabled:opacity-50"
                      >
                        {isRestoring ? 'Restoring…' : 'Restore Version'}
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="text-xs font-mono font-semibold text-brand-vib dark:text-brand-300">
                    Active View
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
